import {ActivitiesGroup} from '../../backend/interfaces/activities-group';
import {ActivitiesSector} from '../../backend/interfaces/activities-sector';
import {EntitySocialResponse} from '../../backend/interfaces/entity-social-response';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {EntityOccurrenceTypeCategory} from '../../backend/responses/entity-occurrence';

export interface DailyCareLogFilters {
  activitiesGroupIds: string[];
  socialResponseIds: string[];
  users: string[];
  status: string[];
  occurrencesTypesGeneral: string[];
  occurrencesTypesPatients: string[];
  attendanceMovements: string[];
  attendanceStatus: string[];
  attendancePeriods: string[];
}

/** Filters of the attendance tracker sessions */
export type AttendanceFilterGroup =
  | 'attendanceMovements'
  | 'attendanceStatus'
  | 'attendancePeriods';

export interface DailyCareLogFiltersState {
  activitiesSectors?: ActivitiesSector[];
  activityGroups: {[id: string]: ActivitiesGroup[]};
  taskFilters: DailyCareLogFilters;
}

export enum FiltersActionType {
  // Data
  SET_ACTIVITIES_SECTORS = 'SET_ACTIVITIES_SECTORS',
  SET_ACTIVITIES_SECTOR_GROUPS = 'SET_ACTIVITIES_SECTOR_GROUPS',

  // Filters logic
  SET_TASKS_FILTERS = 'SET_TASKS_FILTERS',
  TOGGLE_ACTIVITIES_SECTOR_FILTER = 'TOGGLE_ACTIVITIES_SECTOR_FILTER',
  TOGGLE_ACTIVITIES_GROUP_FILTER = 'TOGGLE_ACTIVITIES_GROUP_FILTER',
  TOGGLE_SOCIAL_RESPONSE_FILTER = 'TOGGLE_SOCIAL_RESPONSE_FILTER',
  TOGGLE_ALL_SOCIAL_RESPONSE_FILTERS = 'TOGGLE_ALL_SOCIAL_RESPONSE_FILTERS',
  TOGGLE_OCCURRENCE_TYPE_GENERAL_FILTER = 'TOGGLE_OCCURRENCE_TYPE_GENERAL_FILTER',
  TOGGLE_OCCURRENCE_TYPE_PATIENTS_FILTER = 'TOGGLE_OCCURRENCE_TYPE_PATIENTS_FILTER',
  TOGGLE_USER_FILTER = 'TOGGLE_USER_FILTER',
  TOGGLE_ALL_USER_FILTERS = 'TOGGLE_ALL_USER_FILTERS',
  TOGGLE_STATUS_FILTER = 'TOGGLE_STATUS_FILTER',
  TOGGLE_ALL_STATUS_FILTERS = 'TOGGLE_ALL_STATUS_FILTERS',
  TOGGLE_ALL_OCCURRENCE_TYPE_GENERAL_FILTERS = 'TOGGLE_ALL_OCCURRENCE_TYPE_GENERAL_FILTERS',
  TOGGLE_ALL_OCCURRENCE_TYPE_PATIENTS_FILTERS = 'TOGGLE_ALL_OCCURRENCE_TYPE_PATIENTS_FILTERS',
  TOGGLE_ATTENDANCE_FILTER = 'TOGGLE_ATTENDANCE_FILTER',
  TOGGLE_ALL_ATTENDANCE_FILTERS = 'TOGGLE_ALL_ATTENDANCE_FILTERS',
  CLEAR_ALL_FILTERS = 'CLEAR_ALL_FILTERS',
  TOGGLE_DATE = 'TOGGLE_DATE',
}

export type TasksFiltersAction =
  | {
      type: FiltersActionType.TOGGLE_STATUS_FILTER;
      status: string;
      selected: boolean;
    }
  | {
      type: FiltersActionType.TOGGLE_USER_FILTER;
      userId: string;
      selected: boolean;
    }
  | {
      type: FiltersActionType.SET_ACTIVITIES_SECTORS;
      activitiesSectors: ActivitiesSector[];
    }
  | {
      type: FiltersActionType.SET_ACTIVITIES_SECTOR_GROUPS;
      activitiesSectorId: string;
      activitiesGroups: ActivitiesGroup[];
    }
  | {
      type: FiltersActionType.SET_TASKS_FILTERS;
      tasksFilters: DailyCareLogFilters;
    }
  | {
      type: FiltersActionType.TOGGLE_ACTIVITIES_SECTOR_FILTER;
      activitiesSectorId: string;
      selected: boolean;
    }
  | {
      type: FiltersActionType.TOGGLE_ACTIVITIES_GROUP_FILTER;
      activitiesGroupId: string;
      selected: boolean;
    }
  | {
      type: FiltersActionType.TOGGLE_SOCIAL_RESPONSE_FILTER;
      socialResponseId: string;
      selected: boolean;
    }
  | {
      type: FiltersActionType.TOGGLE_OCCURRENCE_TYPE_GENERAL_FILTER;
      occurrenceType: string;
      selected: boolean;
    }
  | {
      type: FiltersActionType.TOGGLE_OCCURRENCE_TYPE_PATIENTS_FILTER;
      occurrenceType: string;
      selected: boolean;
    }
  | {
      type: FiltersActionType.TOGGLE_ALL_SOCIAL_RESPONSE_FILTERS;
      selected: boolean;
      socialResponses: EntitySocialResponse[];
    }
  | {
      type: FiltersActionType.TOGGLE_ALL_USER_FILTERS;
      selected: boolean;
      users: string[];
    }
  | {
      type: FiltersActionType.TOGGLE_ALL_STATUS_FILTERS;
      selected: boolean;
      status: string[];
    }
  | {
      type: FiltersActionType.TOGGLE_ALL_OCCURRENCE_TYPE_GENERAL_FILTERS;
      selected: boolean;
      occurrenceTypes: string[];
      category: EntityOccurrenceTypeCategory;
    }
  | {
      type: FiltersActionType.TOGGLE_ALL_OCCURRENCE_TYPE_PATIENTS_FILTERS;
      selected: boolean;
      occurrenceTypes: string[];
      category: EntityOccurrenceTypeCategory;
    }
  | {
      type: FiltersActionType.TOGGLE_ATTENDANCE_FILTER;
      group: AttendanceFilterGroup;
      value: string;
      selected: boolean;
    }
  | {
      type: FiltersActionType.TOGGLE_ALL_ATTENDANCE_FILTERS;
      group: AttendanceFilterGroup;
      values: string[];
    }
  | {
      type: FiltersActionType.CLEAR_ALL_FILTERS;
    };

export interface DailyCareLogFiltersType {
  // data
  activitiesSectors: ActivitiesSector[] | undefined;
  tasksFilters: DailyCareLogFilters;
  activityGroups: {[id: string]: ActivitiesGroup[]};
  users?: AffiliatedUser[];

  // data loading
  fetchActivitiesSectors: (entityId: string) => Promise<void>;
  fetchActivitiesGroups: (entityId: string, activitiesSectorId: string) => Promise<void>;

  // filters logic
  setTasksFilters: (tasksFilters: DailyCareLogFilters) => void;
  toggleActivitiesSectorFilter: (activitiesSectorId: string, selected: boolean) => void;
  toggleActivitiesGroupFilter: (activitiesGroupId: string, selected: boolean) => void;
  toggleSocialResponseFilter: (socialResponseId: string, selected: boolean) => void;
  toggleAllSocialResponseFilters: (selected: boolean) => void;
  toggleUserFilter: (userId: string, selected: boolean) => void;
  toggleStatusFilter: (status: string, selected: boolean) => void;
  toggleOccurrenceTypeFilter: (
    occurrenceType: string,
    selected: boolean,
    category: EntityOccurrenceTypeCategory
  ) => void;
  toggleAllUserFilters: (selected: boolean, userIds: string[]) => void;
  toggleAllStatusFilters: (selected: boolean, status: string[]) => void;
  toggleAllOccurrenceTypeFilters: (
    selected: boolean,
    occurrenceTypes: string[],
    category: EntityOccurrenceTypeCategory
  ) => void;
  toggleAttendanceFilter: (group: AttendanceFilterGroup, value: string, selected: boolean) => void;
  toggleAllAttendanceFilters: (group: AttendanceFilterGroup, values: string[]) => void;
  clearAllFilters: () => void;
}
